import type { AssetFilters } from "@/hooks/useAssets";
import { normalizeAssetTags } from "@/lib/normalize-asset-tags";

/** Serializable browse filters — URL is the source of truth. */
export type BrowseLicense = "all" | "free" | "paid";

export type BrowseUrlState = {
  category: string | null;
  q: string;
  sort: string;
  page: number;
  /** Results per page (`per` param) — one of BROWSE_PAGE_SIZES. */
  perPage: number;
  license: BrowseLicense;
  minPrice: number;
  maxPrice: number;
  minRating: number;
  /** Tag filters (OR): assets that have at least one of these tags. */
  tags: string[];
};

export const BROWSE_PAGE_SIZES = [25, 50, 100] as const;

export const BROWSE_DEFAULTS: BrowseUrlState = {
  category: null,
  q: "",
  sort: "newest",
  page: 1,
  perPage: BROWSE_PAGE_SIZES[0],
  license: "all",
  minPrice: 0,
  maxPrice: 200,
  minRating: 0,
  tags: [],
};

function parseIntParam(raw: string | null, fallback: number): number {
  if (raw == null || raw === "") return fallback;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : fallback;
}

function parseFloatParam(raw: string | null, fallback: number): number {
  if (raw == null || raw === "") return fallback;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : fallback;
}

/** Parse Next.js searchParams into browse state (defaults applied). */
export function parseBrowseUrl(searchParams: URLSearchParams): BrowseUrlState {
  const category = searchParams.get("category")?.trim() || null;
  const q = searchParams.get("q")?.trim() || searchParams.get("search")?.trim() || "";
  const sort = searchParams.get("sort")?.trim() || BROWSE_DEFAULTS.sort;
  const page = Math.max(1, parseIntParam(searchParams.get("page"), 1));
  const perRaw = parseIntParam(searchParams.get("per"), BROWSE_DEFAULTS.perPage);
  const perPage = (BROWSE_PAGE_SIZES as readonly number[]).includes(perRaw)
    ? perRaw
    : BROWSE_DEFAULTS.perPage;

  const licenseRaw = searchParams.get("license")?.toLowerCase().trim();
  let license: BrowseLicense = BROWSE_DEFAULTS.license;
  if (licenseRaw === "free" || licenseRaw === "paid" || licenseRaw === "all") {
    license = licenseRaw;
  }
  // Legacy ?free=true
  if (searchParams.get("free") === "true" && license === "all") {
    license = "free";
  }

  const minPrice = Math.max(0, parseFloatParam(searchParams.get("minPrice"), BROWSE_DEFAULTS.minPrice));
  const maxPrice = Math.min(500, Math.max(0, parseFloatParam(searchParams.get("maxPrice"), BROWSE_DEFAULTS.maxPrice)));
  const minRating = Math.max(0, Math.min(5, parseFloatParam(searchParams.get("minRating"), BROWSE_DEFAULTS.minRating)));

  const tagsParam = searchParams.get("tags")?.trim();
  const tags = tagsParam
    ? normalizeAssetTags(
        tagsParam
          .split(",")
          .map((t) => t.trim().slice(0, 50))
          .filter(Boolean),
      ).slice(0, 10)
    : [];

  return {
    category,
    q,
    sort,
    page,
    perPage,
    license,
    minPrice,
    maxPrice: Math.max(minPrice, maxPrice),
    minRating,
    tags,
  };
}

/** Serialize browse state to URLSearchParams (omits defaults). */
export function serializeBrowseUrl(state: BrowseUrlState): string {
  const p = new URLSearchParams();
  if (state.category) p.set("category", state.category);
  if (state.q) p.set("q", state.q);
  if (state.sort && state.sort !== BROWSE_DEFAULTS.sort) p.set("sort", state.sort);
  if (state.page > 1) p.set("page", String(state.page));
  if (state.perPage !== BROWSE_DEFAULTS.perPage) p.set("per", String(state.perPage));
  if (state.license !== BROWSE_DEFAULTS.license) p.set("license", state.license);
  if (state.minPrice > BROWSE_DEFAULTS.minPrice) p.set("minPrice", String(state.minPrice));
  if (state.maxPrice < BROWSE_DEFAULTS.maxPrice) p.set("maxPrice", String(state.maxPrice));
  if (state.minRating > BROWSE_DEFAULTS.minRating) p.set("minRating", String(state.minRating));
  if (state.tags.length > 0) p.set("tags", state.tags.join(","));
  return p.toString();
}

/**
 * Map browse sort to RPC `p_sort_by`. 'relevance' now passes through — the
 * rewritten get_published_assets ranks by relevance when it's set (previously
 * this silently returned 'newest', so the "Relevance" sort option did nothing).
 */
export function toSortRpc(sort: string): NonNullable<AssetFilters["sortBy"]> {
  const allowed = new Set(["relevance", "newest", "popular", "rating", "price_low", "price_high"]);
  if (allowed.has(sort)) return sort as NonNullable<AssetFilters["sortBy"]>;
  return "newest";
}

/** Build `AssetFilters` for `useAssets` / `get_published_assets`. */
export function toAssetFilters(state: BrowseUrlState, searchQuery: string): AssetFilters {
  // With a search term and no explicit sort choice (URL omits the default),
  // rank by relevance — that's what a searching user expects. Picking Popular /
  // Price / Rating still overrides. 'relevance' with no term is meaningless, so
  // only switch when actually searching.
  const effectiveSort =
    searchQuery && state.sort === BROWSE_DEFAULTS.sort ? "relevance" : state.sort;
  const sortBy = toSortRpc(effectiveSort);
  const isFree = state.license === "free" ? true : state.license === "paid" ? false : undefined;

  return {
    category: state.category || undefined,
    search: searchQuery || undefined,
    sortBy,
    minPrice:
      state.license === "free"
        ? undefined
        : state.minPrice > BROWSE_DEFAULTS.minPrice
          ? state.minPrice
          : undefined,
    maxPrice:
      state.license === "free"
        ? undefined
        : state.maxPrice < BROWSE_DEFAULTS.maxPrice
          ? state.maxPrice
          : undefined,
    minRating: state.minRating > 0 ? state.minRating : undefined,
    isFree,
    tags: state.tags.length > 0 ? state.tags : undefined,
  };
}

export type RestrictiveFilterChip = { label: string; description: string };

/** Pick the two most restrictive active filters for empty-state hints (deterministic). */
export function getTopRestrictiveFilters(
  state: BrowseUrlState,
  categoryName: string | null,
): RestrictiveFilterChip[] {
  type Scored = { score: number } & RestrictiveFilterChip;
  const out: Scored[] = [];

  if (state.q.trim()) {
    out.push({
      score: 6,
      label: "Search",
      description: `"${state.q.trim()}"`,
    });
  }
  if (state.category && categoryName) {
    out.push({ score: 5, label: "Category", description: categoryName });
  }
  if (state.license === "paid") {
    out.push({ score: 4, label: "License", description: "Paid only" });
  } else if (state.license === "free") {
    out.push({ score: 4, label: "License", description: "Free only" });
  }
  if (state.minRating > 0) {
    out.push({ score: 3, label: "Rating", description: `${state.minRating}+ stars` });
  }
  if (state.tags.length > 0) {
    out.push({
      score: 3,
      label: "Tags",
      description: state.tags.join(", "),
    });
  }
  if (
    state.license !== "free" &&
    (state.minPrice > BROWSE_DEFAULTS.minPrice || state.maxPrice < BROWSE_DEFAULTS.maxPrice)
  ) {
    out.push({
      score: 2,
      label: "Price",
      description: `$${state.minPrice} – $${state.maxPrice}`,
    });
  }

  out.sort((a, b) => b.score - a.score);
  return out.slice(0, 2).map(({ label, description }) => ({ label, description }));
}

/** Toggle a tag in browse URL state (max 10 tags, deduped). */
export function toggleBrowseTagList(current: string[], tag: string): string[] {
  const t = tag.trim().slice(0, 50);
  if (!t) return current;
  const next = current.includes(t) ? current.filter((x) => x !== t) : [...current, t];
  return normalizeAssetTags(next).slice(0, 10);
}
